import React, {useEffect, useRef, useState} from "react";

import AIResultCustomPromptButtons from "./AIResultCustomPromptButtons";
import {cutImageIntoFourPieces} from "../../aiCutImageIntoPieces";

// The 2x2 grid laid over a "multiImage" AI result (one image built out of 4 variants, see
// isMultiImage in aiApi).
//
// The result is cut into its 4 pieces HERE, in an effect, as soon as the multiImage result is
// shown - not on click - so opening a piece is instant (the cut goes through a canvas and, for
// an s3 image without usable CORS headers, through a full base64 re-fetch, which used to add a
// visible delay to the first click).
//
// The cells themselves are transparent: the underlying <img> / before-after slider of
// LookAIImageOverlayImageContent stays visible through them. A cell only adds the hover state
// and catches the click; its buttons sit in its bottom right corner and stop their own clicks
// from reaching the cell.

type Props = {
  resultImageUrl: string,
  // (pieceIndex, dataUrl, viewOnly) - viewOnly is true when the piece itself was clicked
  // (zoom + before/after slider), false when its "edit" button was (zoom + custom prompt)
  onPieceOpen: (pieceIndex: number, dataUrl: string, viewOnly: boolean) => void,
  onThumbUpClick?: (pieceIndex: number) => void,
  onThumbDownClick?: (pieceIndex: number) => void,
  disabled?: boolean,
};

export default function LookAIMultiImage({
  resultImageUrl,
  onPieceOpen,
  onThumbUpClick,
  onThumbDownClick,
  disabled,
}: Props) {

  const [pieces, setPieces] = useState<string[]>([]);
  // the url the pieces in state were cut from - guards against a late cut of a previous
  // result overwriting the pieces of the one currently on screen
  const cutForUrlRef = useRef<string>("");

  useEffect(() => {
    if (!resultImageUrl || cutForUrlRef.current === resultImageUrl) {
      return;
    }
    let cancelled = false;
    cutForUrlRef.current = resultImageUrl;
    setPieces([]);
    const cutPieces = async () => {
      const nextPieces = await cutImageIntoFourPieces(resultImageUrl);
      if (cancelled || cutForUrlRef.current !== resultImageUrl) {
        return;
      }
      setPieces(nextPieces);
    };
    cutPieces();
    return () => {
      cancelled = true;
    };
  }, [resultImageUrl]);

  if (!pieces.length) {
    return null; // nothing to click on until the pieces are ready
  }

  return (
    <div className="absolute u-left-0 u-top-0 u-w-full u-h-full z-10 look-ai__overlay-pieces">
      {pieces.map((dataUrl, pieceIndex) => (
        <div key={pieceIndex}
             className="relative flex look-ai__overlay-pieces-cell pr-4 pb-4"
             onClick={() => !disabled && onPieceOpen(pieceIndex, dataUrl, true)}>
          <AIResultCustomPromptButtons pieceIndex={pieceIndex}
                                       disabled={disabled}
                                       onEditClick={() => onPieceOpen(pieceIndex, dataUrl, false)}
                                       onThumbUpClick={onThumbUpClick}
                                       onThumbDownClick={onThumbDownClick}/>
        </div>
      ))}
    </div>
  );
}
